import { FC, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateTimePicker,
  RHFTextField,
} from "../../RHFComponents/RHFComponents";
import { FormProvider, useForm } from "react-hook-form";
import lombardiaIcon from "../../../assets/icons/Regione Lombardia Loghi.svg";
import { DataContext } from "./Context/ContextWrapper";
import {
  anaggpPacada,
  carPagp,
  datiPacada,
  ricAnapres,
} from "../../../networking";
import { RecurringBookingContext } from "../../RecurringBookingManager/Context/RecurringBookingContextWrapper";
import type { RecurringBookingContextType } from "../../RecurringBookingManager/Context/RecurringBookingContextWrapper";

type Props = {
  handleClose: (value: boolean) => void;
  open?: boolean;
  onConfirm?: (dataFromRegion?: any) => void;
  onCancel?: () => void;
  body?: any;
  selectedAppointments?: any[];
  overrideIdPacada?: string;
  overrideAziendaPrenotazione?: string;
  isRecurring?: boolean;
  hintsOverride?: Partial<Record<string, string>>;
  selectedAppointmentsItem?: any;
};

type FormValues = {
  specialita: string;
  prestazione: string;
  prestazioneSiss: string;
  agenda: string;
  codNomenclatoreRegionale: string;
  codCatalogoPrescrizione: string;
  idPrescrittore: string;
  provenienza: string;
  tipoRicetta: string;
  tipoPrestazione: string;
  esenzione: string;
  impegnativa: string;
  dataImpegnativa: string;
  iup: string;
  iurp: string;
  classePriorita: string;
  prescrSuggerita: boolean;
  primoAccesso: boolean;
  pic: boolean;
  protPic: string;
  idPai: string;
  diagnosi: string;
  noteAppuntamento: string;
};

const RegionDataDetails: FC<Props> = ({
  handleClose,
  open,
  onConfirm,
  body,
  selectedAppointments,
  overrideAziendaPrenotazione,
  overrideIdPacada,
  isRecurring,
  hintsOverride,
  selectedAppointmentsItem,
}) => {
  // [REG] Stato chiave/valore con i dati regionali caricati (mappa normalizzata)
  const [api, setApi] = useState<Record<string, string>>({});
  // [REG] Stato per gestire il loading durante fetch e submit
  const [loading, setLoading] = useState(false);

  // Converte l'array restituito dall'API (es. [{ campo: "CodiceSISS", dato: "0188741" })
  // in una mappa a chiave/valore da usare nel codice, del tipo:
  // { CodiceSISS: "0188741", DescrizioneSISS: "ECOGRAFIA ADDOME SUPERIORE", ... }.
  // Se un elemento non ha "campo" o "dato", lo saltiamo o usiamo stringa vuota
  //   così da evitare undefined e semplificare i controlli (confronto con 0/1, ecc.).
  const toMap = (list: Array<{ campo?: string; dato?: string }> = []) => {
    const out: Record<string, string> = {};
    list.forEach((item) => {
      if (item?.campo) {
        out[item.campo] = item.dato ?? "";
      }
    });
    return out;
  };

  // [REG] Inizializzazione form (RHForm) con default vuoti
  const methods = useForm<FormValues>({
    defaultValues: {
      specialita: "",
      prestazione: "",
      prestazioneSiss: "",
      agenda: "",
      codNomenclatoreRegionale: "",
      codCatalogoPrescrizione: "",
      idPrescrittore: "",
      provenienza: "",
      tipoRicetta: "",
      tipoPrestazione: "",
      esenzione: "",
      impegnativa: "",
      dataImpegnativa: "",
      iup: "",
      iurp: "",
      classePriorita: "",
      prescrSuggerita: false,
      primoAccesso: false,
      pic: false,
      protPic: "",
      idPai: "",
      diagnosi: "",
      noteAppuntamento: "",
    },
  });

  const { control, reset, watch, formState, setValue, getValues } = methods;

  // contatori che seguono i valori attuali del form
  const diagnosiVal = watch("diagnosi") ?? "";
  const noteVal = watch("noteAppuntamento") ?? "";
  const diagCount = `(${diagnosiVal.length}/400)`;
  const noteCount = `(${noteVal.length}/400)`;

  const context = useContext(DataContext);
  const recurringCtx = useContext(
    RecurringBookingContext
  ) as RecurringBookingContextType;

  const idPacadaEff = overrideIdPacada ?? context?.idPacada;
  const aziendaEff =
    overrideAziendaPrenotazione ?? context?.aziendaPrenotazione ?? "";

  console.log({ context });
  const isCiclica = Boolean(overrideIdPacada);
  useEffect(() => {
    console.log("idPacada: ", idPacadaEff);
  }, []);

  // effetto di caricamento dei dati
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);

        const hintsCtx = context?.regionDataHints?.[idPacadaEff ?? ""] ?? {};
        const hints = { ...hintsCtx, ...(hintsOverride ?? {}) };

        // Se non ho id, mi fermo ai soli hint (niente warning “skip”)
        if (!idPacadaEff) {
          reset((prev) => ({
            ...prev,
            specialita: prev.specialita || hints.specialita || "",
            prestazione: prev.prestazione || hints.prestazione || "",
          }));
          return;
        }

        const impFromForm = (methods.getValues("impegnativa") ?? "").trim();
        const idAnapazFromCtx = String(
          recurringCtx?.confermaPianoTerapiaBody?.idAnapaz ?? ""
        ).trim();

        const payloadCarpagp: any = {
          aziendaPrenotazione: aziendaEff,
          idPacada: idPacadaEff,
        };

        // Se è ciclica, idAnapaz è richiesto. Se manca, NON chiamo carPagp ancora.
        if (isCiclica) {
          if (!idAnapazFromCtx) {
            reset((prev) => ({
              ...prev,
              specialita: prev.specialita || hints.specialita || "",
              prestazione: prev.prestazione || hints.prestazione || "",
              impegnativa: impFromForm || prev.impegnativa || "",
            }));
            setLoading(false);
            return;
          }
          payloadCarpagp.idAnapaz = idAnapazFromCtx ?? "";
          payloadCarpagp.impegnativa = impFromForm ?? "";
        }

        const response = await carPagp(payloadCarpagp);
        if (!alive) return;

        const map = toMap(response?.data?.dati as any[]);
        setApi(map);

        reset({
          specialita: map.Specialita || hints.specialita || "",
          prestazione: map.Prestazione || hints.prestazione || "",
          prestazioneSiss:
            map.DescrizioneSISS || map.CodiceSISS || hints.codSiss || "",
          agenda: map.DescAgendaGP || map.AgendaGP || "",
          codNomenclatoreRegionale: map.CodiceNomenclatoreRegionale || "",
          codCatalogoPrescrizione: map.CodiceCatalogoPrescrizione || "",
          idPrescrittore: map.IdPrescrittore || "",
          provenienza: map.Provenienza || "",
          tipoRicetta: map.TipoRicetta || "",
          tipoPrestazione: map.TipoPrestazione || "",
          esenzione: map.Esenzione || "",
          impegnativa: map.Impegnativa || "",
          dataImpegnativa: map.DataImpegnativa || "",
          iup: map.IUP || "",
          iurp: map.CodiceIURP || "",
          classePriorita: map.ClassePriorita || "",
          prescrSuggerita: (map.PrescrSuggerita ?? "0") === "1",
          primoAccesso: (map.PrimoAccesso ?? "0") === "1",
          pic: (map.PIC ?? "0") === "1",
          protPic: map.ProtPIC || "",
          idPai: map.IDPAI || "",
          diagnosi: map.Diagnosi || "",
          noteAppuntamento: map.NoteAppuntamento || "",
        });
      } catch (e) {
        console.error("[RegionDataDetails] carPagp error:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [idPacadaEff, aziendaEff, hintsOverride, open]);

  //Se si chiude il modale o cambia idPacada, azzero tutto e pulisco
  useEffect(() => {
    return () => {
      // [REG] Azzero mappa e form per evitare bleed di valori
      setApi({});
      reset();
    };
  }, [idPacadaEff]);

  // Mostro i campi se vis == 1 O se esiste già un valore non vuoto.
  // Nascosti solo se vis == 0 E tutti i relativi valori sono vuoti.
  const show = useMemo(() => {
    const is1 = (k: string) => (api[k] ?? "0") === "1";
    const has = (k: string) => (api[k] ?? "").toString().trim() !== "";

    return {
      // Codici nomenclatore / catalogo
      codiciReg:
        is1("VisCodiciReg") ||
        has("CodiceNomenclatoreRegionale") ||
        has("CodiceCatalogoPrescrizione"),

      // Tipo ricetta
      tipoRicetta: is1("visTipoRicetta") || has("TipoRicetta"),

      // Prestazione SISS + Ric.Cod.SISS
      codiceSiss:
        is1("visCodiceSiss") || has("CodiceSISS") || has("DescrizioneSISS"),

      // Agenda GP + Ric.Agende
      agendaGp: is1("visAgendaGP") || has("AgendaGP") || has("DescAgendaGP"),
    };
  }, [api]);

  const prescrittoreResearch = async () => {
    console.log("Ricerca Prescrittore");
  };

  const codeSissResearch = async () => {
    console.log(context?.appointmentsList);

    if (!context) return;
    const first = context.appointmentsList?.[0];
    if (!first) return;
    const payloadSiss = {
      aziendaPrenotazione: aziendaEff,
      dataRif: selectedAppointmentsItem.data,
      codSpecia: selectedAppointmentsItem.descrizioneSpecialita,
      codAnapre: selectedAppointmentsItem.tipoAgenda,
      codAnapres: selectedAppointmentsItem.descrizionePrestazione,
      testValidita: "S",
      flDisattivati: "S",
    };

    const responseSiss = await ricAnapres(payloadSiss);
    const dataSiss = responseSiss.data.dati;

    if (dataSiss.length > 0) {
      setValue("prestazioneSiss", String(dataSiss));
    }

    console.log(responseSiss.data.dati);
  };

  useEffect(() => {
    return () => {
      setApi({});
      reset();
    };
  }, []);

  const agendaResearch = async () => {
    if (!context) return;
    const first = context.appointmentsList?.[0];
    if (!first) return;

    const payloadAgenda = {
      aziendaPrenotazione: aziendaEff,
      dataRif: selectedAppointmentsItem.data,
      codSpecia: selectedAppointmentsItem.descrizioneSpecialita,
      codAnapre: selectedAppointmentsItem.descrizionePrestazione,
      codAnapres: getValues("prestazioneSiss") || "",
      codMedint: selectedAppointmentsItem.medico,
      codTpgpre: selectedAppointmentsItem.tipoAgenda,
      codPgmedt: selectedAppointmentsItem.medico,
    };

    const responseAgenda = await anaggpPacada(payloadAgenda);
    const datiAgenda = responseAgenda.data.dati;

    console.log({ responseAgenda });

    if (datiAgenda.length > 0) {
      setValue("agenda", String(datiAgenda));
    }
  };

  useEffect(() => {
    console.log({ api });
  }, [api]);

  // subito sotto const context = useContext(DataContext);
  useEffect(() => {
    if (!overrideIdPacada) return;
    const hintsCtx = context?.regionDataHints?.[overrideIdPacada ?? ""] ?? {};
    const hints = { ...hintsCtx, ...(hintsOverride ?? {}) };

    reset((prev) => ({
      ...prev,
      specialita: hints.specialita ?? prev.specialita ?? "",
      prestazione: hints.prestazione ?? prev.prestazione ?? "",
    }));
    // non serve loading qui
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, overrideIdPacada, hintsOverride]);

  // Per aggiornare i dati regionali attraverso datiPacada
  const onSubmit = async (values: FormValues) => {
    try {
      setLoading(true);
      // prova a leggere dallo stato api, altrimenti stringa vuota
      const get = (k: string) => (api?.[k] ?? "").toString();
      if (isRecurring) {
        // Costruisco un payload adattato alle prenotazioni cicliche con le chiavi leggibili dal padre
        const region = {
          codNomenclatoreRegionale: values.codNomenclatoreRegionale ?? "",
          codCatalogoPrescrizione: values.codCatalogoPrescrizione ?? "",
          tipoRicetta: values.tipoRicetta ?? "",
          esenzione: values.esenzione ?? "",
          impegnativa: values.impegnativa ?? "",
          dataImpegnativa: values.dataImpegnativa ?? "",
          iup: values.iup ?? "",
          iurp: values.iurp ?? "",
          classePriorita: values.classePriorita ?? "",
          prescrSuggerita: values.prescrSuggerita ? "1" : "0",
          primoAccesso: values.primoAccesso ? "1" : "0",
          pic: values.pic ? "1" : "0",
          protPic: values.protPic ?? "",
          idPai: values.idPai ?? "",
          diagnosi: values.diagnosi ?? "",
          notePrestazione: values.noteAppuntamento ?? "",
          codPrestSISS: values.prestazioneSiss ?? "",
          codAgendaGP: values.agenda ?? "",
          prescrittore: values.idPrescrittore ?? "",
          provenienzaPaziente: values.provenienza ?? "",
          tipoPrestazione: values.tipoPrestazione ?? "",
        };

        onConfirm?.({
          idAppuntamento: String(overrideIdPacada ?? ""),
          region,
        });

        setLoading(false);
        return;
      }

      const payload = {
        idPacada: idPacadaEff ?? "",
        crud: "U",
        PACADASWTEMP: "",
        PACADASWPRIMARIO: "",
        CODMEDINT: get("CodMedint"),
        DATAVISITA: get("DataVisita"),
        ORAVISITA: get("OraVisita"),
        DURATA: get("Durata"),
        CODSPECIA: values.specialita || get("CodSpecia"),
        CODANAPRE: values.prestazione || get("CodAnapre"),
        CODTPGPRE: get("CodTpgpre"),
        CODCLSPRT: values.classePriorita || get("CodClsPrt"),
        CODIDTPRE: get("CodIdtPre"),
        CODTPRICE: values.tipoRicetta || get("CodTpRice"),
        IMPEGNATIVA: values.impegnativa ?? "",
        DATAIMPEGNATIVA: values.dataImpegnativa ?? "",
        DATA1DISP: get("Data1Disp"),
        IUP: values.iup ?? "",
        CODESENZ: values.esenzione || "",
        CODTIPRAM: values.tipoPrestazione || "",
        CODPGMEDT: get("CodPgMedt"),
        COMPETENZA: get("Competenza"),
        DIAGNOSI: values.diagnosi ?? "",
        NOTE: values.noteAppuntamento ?? "",
        SWFORZATURA: "",
        SWUSUFRUITO: "",
        SWARRIVATO: "",
        DATAARRIVO: "",
        ORAARRIVO: "",
        SWASSENZA: "",
        MEDSOSTITUITO: "",
        MEDRELAZIONATO: "",
        IDACCETTAZIONE: "",
        PRESCRSUGGERITA: values.prescrSuggerita ? "1" : "0",
        FLAGALTRO: "",
        FLAGPIC: values.pic ? "1" : "0",
        IDPAI: values.idPai ?? "",
        PROTOCOLLOPIC: values.protPic ?? "",
        TOLLGGATTESA: "",
        APPROFONDIMENTO: "",
        PRIMOACCESSO: values.primoAccesso ? "1" : "0",
        IURP: values.iurp ?? "",
        CODANAPRES: values.prestazioneSiss ?? "",
        IDAPPGP: "",
        CODANAGGP: "",
        IDPROGCOMBINATO: "",
        CODISTCOM: "",
        CODGPMETOD: "",
        CODGPQUDIA: "",
        CODGPDISTR: "",
        CODNOMENCLREG: values.codNomenclatoreRegionale ?? "",
        CODCATALOGOPRESCR: values.codCatalogoPrescrizione ?? "",
        PRESCRITTORE: values.idPrescrittore ?? "",
        CODOPERATORE: get("CodOperatore"),
        AZIENDAPRENOTAZIONE: context?.aziendaPrenotazione ?? "",
        OGGSPERIMENTAZIONE: "",
        FLAGPREZZO: "",
      };
      await datiPacada(payload);
      handleClose(false);
    } catch (e) {
      console.error("[RegionDataDetails] datiPacada error:", e);
      if (!isRecurring) handleClose?.(true);
    } finally {
      setLoading(false);
    }
  };

  const watchSpecialita = watch("specialita");
  const watchPrestazione = watch("prestazione");

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1300,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80vw",
          maxWidth: 980,
          height: "85vh",
          overflowY: "auto",
          p: 2,
          borderRadius: "4px",
          boxShadow: 24,
          zIndex: 1200,
          bgcolor: "background.paper",
        }}
      >
        {/* titolo */}
        <Grid container mb={2}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontSize={14}
              fontFamily="Rubik"
              color="rgba(0,0,0,0.60)"
              fontWeight={500}
            >
              Dati Regionali
            </Typography>
          </Grid>
        </Grid>

        {/* form due colonne */}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {/* Specialità / Prestazione */}
              <Grid item xs={12}>
                <Box
                  sx={{ border: "1px solid #CCCCCC", borderRadius: 1, p: 2 }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Tooltip
                        title={(watchSpecialita ?? "").trim()}
                        enterDelay={300}
                      >
                        <span>
                          <RHFTextField
                            control={control}
                            name={"specialita"}
                            label="Specialità"
                            fullWidth
                            size="small"
                            disabled
                          />
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Tooltip
                        title={(watchPrestazione ?? "").trim()}
                        enterDelay={300}
                      >
                        <span>
                          <RHFTextField
                            control={control}
                            name={"prestazione"}
                            label="Prestazione"
                            fullWidth
                            size="small"
                            disabled
                          />
                        </span>
                      </Tooltip>
                    </Grid>

                    {/* Prestazione SISS + Ric.Cod.SISS / Agenda + Ric.Agende */}
                    {show.codiceSiss && (
                      <>
                        <Grid item xs={12} md={6}>
                          <RHFTextField
                            control={control}
                            name="prestazioneSiss"
                            label="Prestazione SISS"
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Button
                            variant="outlined"
                            onClick={codeSissResearch}
                            sx={{ height: 40 }}
                          >
                            Ric.Cod.SISS
                          </Button>
                        </Grid>
                      </>
                    )}
                    {show.agendaGp && (
                      <>
                        <Grid item xs={12} md={6}>
                          <RHFTextField
                            control={control}
                            name="agenda"
                            label="Agenda"
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Button
                            variant="outlined"
                            onClick={agendaResearch}
                            sx={{ height: 40 }}
                          >
                            Ric.Agende
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Grid>

              {/* Codici nomenclatore / catalogo prescrizione */}
              {show.codiciReg && (
                <>
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      control={control}
                      name="codNomenclatoreRegionale"
                      label="Cod. nomenclatore regionale"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      control={control}
                      name="codCatalogoPrescrizione"
                      label="Cod. catalogo prescrizione"
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </>
              )}

              {/* ID prescrittore + Ricerca Prescrittore */}
              <Grid item xs={12} md={6}>
                <Grid item xs>
                  <RHFTextField
                    control={control}
                    name={"idPrescrittore"}
                    label="ID prescrittore"
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
              <Grid item md={6}>
                <Button
                  variant="outlined"
                  onClick={prescrittoreResearch}
                  sx={{ height: 40 }}
                >
                  Ricerca Prescrittore
                </Button>
              </Grid>

              {/* Provenienza / Tipo ricetta (select look) */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  control={control}
                  name={"provenienza"}
                  label="Provenienza"
                  fullWidth
                  size="small"
                  multiple
                ></RHFAutocomplete>
              </Grid>
              {show.tipoRicetta && (
                <Grid item xs={12} md={6}>
                  <RHFAutocomplete
                    control={control}
                    name="tipoRicetta"
                    label="Tipo Ricetta"
                    fullWidth
                    size="small"
                    multiple
                  />
                </Grid>
              )}

              {/* Tipo prestazione / Esenzione */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  control={control}
                  name={"tipoPrestazione"}
                  label="Tipo Prestazione"
                  fullWidth
                  size="small"
                  multiple
                ></RHFAutocomplete>
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  control={control}
                  name={"esenzione"}
                  label="Esenzione"
                  fullWidth
                  size="small"
                  multiple
                ></RHFAutocomplete>
              </Grid>

              {/* Impegnativa / Data Impegnativa + icona Lombardia */}
              {/* Impegnativa */}
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"impegnativa"}
                  label="Impegnativa"
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Data impegnativa + icona (stessa colonna md=6 dell'Esenzione) */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs>
                    <RHFDateTimePicker
                      name="dataImpegnativa"
                      control={control}
                      dateOnly
                      label="Data Impegnativa"
                      size="small"
                      fullWidth
                      // se serve: passa lo stile label al TextField interno
                      // slotProps={{ textField: { sx: labelSX, size: 'small' } }}
                    />
                  </Grid>

                  <Grid item>
                    <Box
                      component="button"
                      type="button"
                      sx={{
                        width: 40,
                        height: 40,
                        p: 0,
                        m: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={lombardiaIcon}
                        alt="Regione Lombardia"
                        style={{ width: 40, height: 40 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* IUP / IURP */}
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"iup"}
                  label="IUP"
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"iurp"}
                  label="IURP"
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Classe Priorità + checkbox row */}
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  control={control}
                  name={"classePriorita"}
                  label="Classe Priorità"
                  fullWidth
                  size="small"
                  multiple
                ></RHFAutocomplete>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                alignItems="center"
                gap={3}
                sx={{ pl: { md: 1 } }}
              >
                <RHFCheckbox
                  control={control}
                  name={"prescrSuggerita"}
                  label="Prescr. suggerita"
                />
                <RHFCheckbox
                  control={control}
                  name={"primoAccesso"}
                  label="Primo accesso"
                />
                <RHFCheckbox control={control} name={"pic"} label="PIC" />
              </Grid>

              {/* Prot PIC / ID Pai */}
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"protPic"}
                  label="Prot PIC"
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"idPai"}
                  label="ID Pai"
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Diagnosi / Note appuntamento (textarea + counters) */}
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"diagnosi"}
                  label="Diagnosi"
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 400 }}
                  helperText={diagCount}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RHFTextField
                  control={control}
                  name={"noteAppuntamento"}
                  label="Note appuntamento"
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 400 }}
                  helperText={noteCount}
                />
              </Grid>
            </Grid>
            {/* footer con bottoni */}
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="flex-start"
              >
                <Button
                  variant="outlinedError"
                  onClick={() => handleClose(false)}
                >
                  Uscita
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="flex-end"
              >
                <Button
                  variant="contained"
                  type="submit"
                  disabled={methods.formState.isSubmitting || loading}
                >
                  Conferma
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </>
  );
};

export default RegionDataDetails;
